import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

/**
 * Iter-158.0 — Beta landing.
 *
 * Lists Productions the beta user owns. Kept intentionally spare —
 * the beta area is a working scaffold, not a marketing surface. No
 * legacy nav appears here; a small "Back to legacy app" affordance
 * keeps the escape hatch clear.
 */
export default function BetaHome() {
    const nav = useNavigate();
    const [productions, setProductions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [name, setName] = useState("");
    const [showCreate, setShowCreate] = useState(false);

    const refresh = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/beta/productions");
            setProductions(Array.isArray(data) ? data : []);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        refresh();
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        if (!name.trim()) return;
        setCreating(true);
        try {
            const { data } = await api.post("/beta/productions", { name: name.trim() });
            setName("");
            setShowCreate(false);
            nav(`/beta/productions/${data.id}`);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setCreating(false);
        }
    };

    return (
        <div data-testid="beta-home" className="min-h-screen bg-neutral-950 text-neutral-100 px-4 py-6 sm:px-8 sm:py-10">
            <header className="max-w-3xl mx-auto flex items-center justify-between mb-8">
                <div>
                    <p className="text-[11px] uppercase tracking-[0.24em] text-amber-300/80">Multi-department beta</p>
                    <h1 className="text-2xl sm:text-3xl font-semibold mt-1">Productions</h1>
                </div>
                <Link
                    data-testid="beta-back-to-legacy"
                    to="/"
                    className="tap text-[12px] uppercase tracking-[0.2em] text-neutral-400 hover:text-neutral-200"
                >
                    ← Legacy app
                </Link>
            </header>

            <div className="max-w-3xl mx-auto space-y-4">
                {loading ? (
                    <p className="text-sm text-neutral-500">Loading productions…</p>
                ) : productions.length === 0 ? (
                    <div className="rounded-xl border border-neutral-800 bg-neutral-900/60 p-6 text-center">
                        <p className="text-sm text-neutral-300">No productions yet.</p>
                        <p className="text-[12px] text-neutral-500 mt-1">Create one to start scaffolding shared script + department workspaces.</p>
                    </div>
                ) : (
                    <ul className="space-y-2">
                        {productions.map((p) => (
                            <li key={p.id}>
                                <Link
                                    data-testid={`beta-production-row-${p.id}`}
                                    to={`/beta/productions/${p.id}`}
                                    className="tap block rounded-xl border border-neutral-800 bg-neutral-900/60 p-4 hover:bg-neutral-900"
                                >
                                    <p className="text-sm font-medium text-neutral-100">{p.name}</p>
                                    <p className="text-[12px] text-neutral-500 mt-0.5">
                                        {p.project_count} {p.project_count === 1 ? "project" : "projects"}
                                    </p>
                                </Link>
                            </li>
                        ))}
                    </ul>
                )}

                {showCreate ? (
                    <form onSubmit={submit} className="rounded-xl border border-neutral-800 bg-neutral-900/60 p-4 space-y-3">
                        <label className="block text-[11px] uppercase tracking-[0.2em] text-neutral-400">Production name</label>
                        <input
                            data-testid="beta-new-production-name"
                            autoFocus
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="w-full bg-neutral-950 border border-neutral-800 rounded-lg px-3 py-2 text-sm text-neutral-100 focus:outline-none focus:border-neutral-600"
                            placeholder="e.g. Meantime, Series 2"
                        />
                        <div className="flex gap-2">
                            <button
                                type="submit"
                                data-testid="beta-new-production-submit"
                                disabled={creating || !name.trim()}
                                className="tap px-4 h-9 rounded-full text-[12px] uppercase tracking-[0.2em] bg-amber-500/15 text-amber-200 border border-amber-500/30 hover:bg-amber-500/25 disabled:opacity-50"
                            >
                                {creating ? "Creating…" : "Create"}
                            </button>
                            <button
                                type="button"
                                onClick={() => { setShowCreate(false); setName(""); }}
                                className="tap px-4 h-9 rounded-full text-[12px] uppercase tracking-[0.2em] text-neutral-400 hover:text-neutral-200"
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                ) : (
                    <button
                        type="button"
                        data-testid="beta-new-production-open"
                        onClick={() => setShowCreate(true)}
                        className="tap w-full rounded-xl border border-dashed border-neutral-700 hover:border-neutral-500 p-4 text-sm text-neutral-300"
                    >
                        + New Production
                    </button>
                )}
            </div>
        </div>
    );
}
